import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import step1 from "../../../assets/images/help/step1.png";
import step2 from "../../../assets/images/help/step2.png";
import step3 from "../../../assets/images/help/step3.png";
import step4 from "../../../assets/images/help/step4.png";
import step5 from "../../../assets/images/help/step5.png";
import step6 from "../../../assets/images/help/step6.png";
import step7 from "../../../assets/images/help/step7.png";
import step8 from "../../../assets/images/help/step8.png";
import walkthroughVideo from "../../../assets/help/chartmind-walkthrough.mp4";
import walkthroughCaptions from "../../../assets/help/chartmind-walkthrough.vtt";
import walkthroughPoster from "../../../assets/help/chartmind-walkthrough-poster.jpg";
import HelpVideo from "../../common/HelpVideo";

const HELP_STEPS = [
  {
    img: step1,
    label: "Step 1",
    title: "Start Recording",
    text: "Click the microphone icon to start recording. Speak with your patient normally.",
  },
  {
    img: step2,
    label: "Step 2",
    title: "Stop the Recording",
    text: "When the encounter is finished, click the microphone icon again to stop.",
  },
  {
    img: step3,
    label: "Step 3",
    title: "Go to the Next Step",
    text: "Click the button in the bottom right to proceed to the next step.",
  },
  {
    img: step4,
    label: "Step 4",
    title: "Select a Diagnosis",
    text: "The system will automatically suggest likely diagnoses. Click to select your final diagnosis, or use the eye icon to hide ones that don't fit.",
  },
  {
    img: step5,
    label: "Step 5",
    title: "Select a Treatment Plan",
    text: "Click treatments to select them for your plan.",
  },
  {
    img: step6,
    label: "Step 6",
    title: "Select Discharge Instructions",
    text: "Review and edit patient-friendly discharge instructions. Click edit to customize any section.",
  },
  {
    img: step7,
    label: "Step 7",
    title: "Review Your Clinical Note",
    text: "Review, edit, or copy your finalized clinical notes.",
  },
  {
    img: step8,
    label: "Step 8",
    title: "PeerView Consultation",
    text: 'Select "PeerView Consultation" at any time to ask any questions about the encounter.',
  },
];

const StepCard = ({ img, label, title, text }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 1,
      textAlign: "center",
      alignItems: "center",
    }}
  >
    <Box
      sx={{
        width: "100%",
        p: 1,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "action.hover",
      }}
    >
      <Box
        component="img"
        src={img}
        alt={title}
        sx={{
          width: "100%",
          height: "auto",
          display: "block",
          borderRadius: 1,
          boxShadow: 2,
        }}
      />
    </Box>
    <Box>
      <Typography variant="overline" color="primary" sx={{ fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
        {text}
      </Typography>
    </Box>
  </Box>
);

const ChartMindHelpDialog = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" scroll="paper">
        <DialogTitle
          sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pb: 2 }}
          component="div"
        >
          <Box>
            <Typography variant="h6" component="span">
              How to use ChartMind
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontWeight: 400 }}>
              Record the encounter, select a diagnosis, generate clinical notes.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ ml: 2, mt: 0.25 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 3 }}>
          {/* Narrated walkthrough — generated by the helpvideo pipeline
              (tests-e2e/helpvideos). To refresh it, rebuild and re-copy the MP4
              + VTT into src/assets/help/. */}
          <HelpVideo
            src={walkthroughVideo}
            captions={walkthroughCaptions}
            poster={walkthroughPoster}
            caption="Watch a quick walkthrough to see how ChartMind works."
            sx={{ mt: 1, mb: 5 }}
          />

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 3,
            }}
          >
            {HELP_STEPS.map((step) => (
              <Box key={step.label} sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(33.33% - 16px)" } }}>
                <StepCard {...step} />
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
  );
};

export default ChartMindHelpDialog;
